import { DateRange, Goal, GoalType, Habit, TaskType } from '@griply/types';
import {
  filterOutArchivedGoals,
  hasScheduleInPeriod,
  sortGoals,
} from '@griply/utils';
import { isAfter } from 'date-fns';
import { useGoals, useSubGoals } from 'features/goals';
import { useTasksByGoalId } from 'features/tasks';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { GoalInsightsCard } from 'shared/components/connected/goal-insights-card';
import { PageSectionBase } from 'shared/components/ui/page-section-base';
import { useTaskCounts } from 'shared/contexts/task-counts/use-task-counts';
import { useToday } from 'shared/contexts/today';
import { useEntityLinks } from 'shared/hooks/use-entity-links';
import { useUser } from 'shared/hooks/use-user';
import { getSortedTasks } from 'shared/utils/get-sorted-tasks';
import { getWeekStartsOn } from 'shared/utils/get-week-starts-on';

import { HabitInsightsCard } from '../habit-insights-card';
import * as Styled from './progress-grid.style';

export type ProgressGridProps = {
  goal: Goal;
  dateRange: DateRange;
  isAllRange?: boolean;
};

type SubGoalGridProps = {
  goals: Goal[];
  dateRange: DateRange;
  isAllRange?: boolean;
};

const SubGoalGrid: React.FC<SubGoalGridProps> = ({
  goals,
  dateRange,
  isAllRange,
}) => {
  return (
    <Styled.ChartContainer>
      <Styled.ChartGrid>
        {goals.map((subGoal) => (
          <Styled.ChartItem key={subGoal.id}>
            <GoalInsightsCard
              goal={subGoal}
              startDate={dateRange.startDate}
              endDate={dateRange.endDate}
              hideTimeframeDifference={isAllRange}
              hasBigTitle
              canUpdateProgress
              isLink
              showLeftAxis
            />
          </Styled.ChartItem>
        ))}
      </Styled.ChartGrid>
    </Styled.ChartContainer>
  );
};

export const ProgressGrid: React.FC<ProgressGridProps> = ({
  goal,
  dateRange,
  isAllRange,
}) => {
  const { t } = useTranslation();
  const today = useToday();

  const goals = useGoals();
  const links = useEntityLinks();
  const user = useUser();
  const taskCounts = useTaskCounts();

  const subGoals = useSubGoals(goal.id, true);
  const subtreeGoalIds = useMemo(
    () => [goal.id, ...subGoals.map(({ id }) => id)],
    [goal.id, subGoals],
  );
  const tasks = useTasksByGoalId(subtreeGoalIds);

  const sortedSubGoals = useMemo(
    () =>
      sortGoals(filterOutArchivedGoals(subGoals), {
        allGoals: goals,
        sortMode: goal?.goalSortingMode,
        customOrder: goal?.goalSorting,
        invertStartDateSorting: true,
        taskCounts: taskCounts?.byGoalId,
        goalProgressCounts: taskCounts?.byGoalProgress,
        links,
      }),
    [
      goal?.goalSorting,
      goal?.goalSortingMode,
      goals,
      links,
      subGoals,
      taskCounts?.byGoalId,
      taskCounts?.byGoalProgress,
    ],
  );

  const goalHabits = useMemo(
    () =>
      getSortedTasks(
        tasks.filter(({ type }) => type === TaskType.Habit),
        {
          today,
          weekStartsOn: getWeekStartsOn(user),
          mode: goal?.taskSortingMode ? [goal?.taskSortingMode] : undefined,
          order: [
            ...(goal?.sections?.map(({ tasks }) => tasks).flat(1) ?? []),
            ...(goal?.taskSorting ?? []),
          ],
        },
      ) as Habit[],
    [
      goal?.sections,
      goal?.taskSorting,
      goal?.taskSortingMode,
      tasks,
      today,
      user,
    ],
  );

  const periodSubGoals = useMemo(
    () =>
      isAllRange
        ? sortedSubGoals
        : sortedSubGoals.filter(
            ({ startDate }) =>
              !startDate || !isAfter(startDate, dateRange.endDate),
          ),
    [isAllRange, sortedSubGoals, dateRange.endDate],
  );

  const periodGoals = useMemo(
    () => periodSubGoals.filter(({ type }) => type !== GoalType.Project),
    [periodSubGoals],
  );

  const periodProjects = useMemo(
    () => periodSubGoals.filter(({ type }) => type === GoalType.Project),
    [periodSubGoals],
  );

  const periodHabits = useMemo(
    () =>
      isAllRange
        ? goalHabits
        : goalHabits.filter(({ schedules }) =>
            hasScheduleInPeriod(
              schedules,
              dateRange.startDate,
              dateRange.endDate,
            ),
          ),
    [isAllRange, goalHabits, dateRange.startDate, dateRange.endDate],
  );

  return (
    <>
      {!!periodGoals.length && (
        <PageSectionBase
          title={t('pages.goal-detail.progress.sections.goals')}
          noPadding
        >
          <SubGoalGrid
            goals={periodGoals}
            dateRange={dateRange}
            isAllRange={isAllRange}
          />
        </PageSectionBase>
      )}

      {!!periodProjects.length && (
        <PageSectionBase
          title={t('pages.goal-detail.progress.sections.projects')}
          noPadding
        >
          <SubGoalGrid
            goals={periodProjects}
            dateRange={dateRange}
            isAllRange={isAllRange}
          />
        </PageSectionBase>
      )}

      {!!periodHabits.length && (
        <PageSectionBase
          title={t('pages.goal-detail.progress.sections.habits')}
          noPadding
        >
          <Styled.ChartContainer>
            <Styled.ChartGrid>
              {periodHabits.map((habit) => (
                <Styled.ChartItem key={habit.id}>
                  <HabitInsightsCard
                    habit={habit}
                    startDate={dateRange.startDate}
                    endDate={dateRange.endDate}
                  />
                </Styled.ChartItem>
              ))}
            </Styled.ChartGrid>
          </Styled.ChartContainer>
        </PageSectionBase>
      )}
    </>
  );
};
